import CodePreview, { detectLanguage, Language, renderLanguageName } from "@components/llama/Code/CodePreview";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy } from "@phosphor-icons/react";
import { classNames } from "./utils/classNames";
import { ComboboxSingleSelect } from "./Combobox";
import yaml from "yaml";
import { uniq } from "lodash";
import Tooltip from "./Tooltip";
import { UnstyledButtonBase } from "./Button";
import { JSONViewer } from "@components/llama/Code/JSONViewer";
import { SafeMarkdownStyledWithCode } from "@components/llama/SafeMarkdown";

type FormattableLanguage = Extract<
  Language,
  "json" | "yaml" | "typescript" | "python" | "markdown" | "plaintext" | "json-collapsible"
>;

const FORMATTABLE_LANGUAGES: FormattableLanguage[] = [
  "json",
  "yaml",
  "typescript",
  "python",
  "markdown",
  "plaintext",
  "json-collapsible",
];
type FormatOption = {
  id: FormattableLanguage;
  name: string;
};

const LANGUAGE_RELEVANCE_THRESHOLD = 20;
const tryDetectLanguage = (text: string): [FormattableLanguage, any] => {
  // Use highlight.js to try and detect the language of the input.
  const { language, relevance, secondBest } = detectLanguage(text);
  // console.log("language:", language, "relevance:", relevance, "secondBest:", secondBest);

  const testLanguageChoice = (
    language: Language,
    relevance: number,
    backup: [FormattableLanguage, any],
  ): [FormattableLanguage, any] => {
    try {
      if (relevance < LANGUAGE_RELEVANCE_THRESHOLD) {
        return backup;
      }

      if (language === "json") {
        const value = JSON.parse(text);
        return ["json", value];
      } else if (language === "yaml") {
        const value = yaml.parse(text);
        return ["yaml", value];
      } else if (FORMATTABLE_LANGUAGES.includes(language as FormattableLanguage)) {
        return [language as FormattableLanguage, text];
      }
    } catch (error) {
      return backup;
    }

    return backup;
  };

  // If we can't detect the language or on error, return the text as plaintext.
  return testLanguageChoice(
    language,
    relevance,
    secondBest
      ? testLanguageChoice(secondBest.language, secondBest.relevance, ["plaintext", text])
      : ["plaintext", text],
  );
};

export type LanguageSelectLocation = "top-left" | "top-right" | "inside-right";

/** FormattedText with inferred initial language. */
export const AutoFormattedText = ({
  text,
  languageSelectLocation,
  containerClassName,
  onLanguageChange,
}: {
  text: string;
  languageSelectLocation?: LanguageSelectLocation;
  containerClassName?: string;
  onLanguageChange?: (language: FormattableLanguage) => void;
}): React.ReactNode => {
  const [detectedLanguage, value] = tryDetectLanguage(text);
  const [languageOverride, setLanguageOverride] = useState<FormattableLanguage | null>(null);

  // TODO: Consider persisting preference for data languages when we have a clear way
  // of communicating that the language has been converted; otherwise it could be surprising.
  // const [lastViewedDataLanguage, setLastViewedDataLanguage] = useLocalStorageState<FormattableLanguage | null>(
  //   "formatted-data-language",
  //   {
  //     defaultValue: null,
  //   },
  // );

  useEffect(() => {
    setLanguageOverride(null);
  }, [detectedLanguage]);

  const handleLanguageChange = useCallback(
    (language: FormattableLanguage) => {
      setLanguageOverride(language);
      onLanguageChange?.(language);
    },
    [onLanguageChange],
  );

  const renderLanguage = languageOverride ?? detectedLanguage;
  const renderValue = ["json", "yaml"].includes(renderLanguage) ? value : text;

  // Don't show the dropdown component, or potentially render it as an empty block element, if the text is empty.
  // This happens often if the assistant is a tool call with no text, we don't want the text part to show a dropdown.
  if (text === "") {
    return null;
  }

  return (
    <FormattedData
      key={`code-block`}
      initialLanguage={renderLanguage}
      supportedLanguages={uniq(["plaintext", "markdown", "json", "yaml", detectedLanguage])}
      value={renderValue}
      containerClassName={containerClassName}
      onLanguageChange={handleLanguageChange}
      languageSelectLocation={languageSelectLocation}
    />
  );
};

/** Data display with language selection */
export const FormattedData = ({
  initialLanguage,
  onLanguageChange,
  supportedLanguages = ["plaintext", "markdown", "json", "yaml", "python", "typescript", "json-collapsible"],
  value,
  containerClassName,
  languageSelectLocation = "inside-right",
}: {
  initialLanguage: FormattableLanguage;
  onLanguageChange?: (language: FormattableLanguage) => void;
  supportedLanguages?: FormattableLanguage[];
  value: any;
  containerClassName?: string;
  languageSelectLocation?: LanguageSelectLocation;
}) => {
  const [language, setLanguage] = useState<FormattableLanguage>(initialLanguage);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  // Update internal state when initialLanguage changes
  useEffect(() => {
    setLanguage(initialLanguage);
  }, [initialLanguage]);

  const codeString = useMemo(() => {
    return typeof value === "string" ? value : JSON.stringify(value, null, 2);
  }, [value]);

  const handleSelect = async (value: FormattableLanguage) => {
    setLanguage(value);
    onLanguageChange?.(value);
  };

  const languageToOption = (language: FormattableLanguage): FormatOption => {
    return {
      id: language,
      name: renderLanguageName(language),
    };
  };

  const options: FormatOption[] =
    supportedLanguages.map((option) => {
      return languageToOption(option);
    }) ?? [];

  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(codeString);
    setShowCopySuccess(true);
    setTimeout(() => {
      setShowCopySuccess(false);
    }, 2000);
  };

  const codePreviewProps: Partial<React.ComponentProps<typeof CodePreview>> = {
    className: "text-12-12",
    noPadding: true,
    codeTagProps: { className: "!whitespace-pre-wrap" },
  };

  return (
    <div
      className={classNames(
        "scrollbar-transparent-track group pointer-events-none relative w-full rounded-ms text-left",
        containerClassName,
        // Negative margin (-32px) so that this div can surround the role tag on messages such that the language select can be aligned with it.
        // And see the text area for more margin fun to position the text part correctly.
        languageSelectLocation.startsWith("top") ? "-mt-32" : "",
      )}
    >
      <div className={classNames("pointer-events-none sticky z-50 h-24 ")}>
        <div
          className={classNames(
            `z-50 flex`,
            languageSelectLocation.endsWith("right")
              ? // pr-8 is because of the multicolumn eval Review view... we don't want this to be right on the scrollbar.
                // Ideally we'd not do this in all cases, but this is simpler.
                "justify-end pr-8"
              : "-ml-4",
          )}
        >
          <div
            className={classNames(
              "pointer-events-auto relative flex items-center gap-2 rounded-ms bg-background-base-1 p-2 shadow-card-sm transition-all delay-100 duration-150 ease-in-out group-hover:visible group-hover:opacity-100",
              comboboxOpen ? "visible opacity-100" : "invisible opacity-0",
            )}
          >
            <ComboboxSingleSelect<FormatOption>
              id="text-language-combobox"
              placeholder={language}
              wrapperClassName={"rounded-ms border border-transparent px-6 hover:bg-background-base-3"}
              optionsClassName=" -ml-[9px] !min-w-[136px]"
              className="border-none"
              options={options}
              selected={languageToOption(language)}
              onSelect={(option: FormatOption) => handleSelect(option.id)}
              showSearchIcon={true}
              renderOption={(option: FormatOption) => ({
                id: option.name,
                name: option.name,
              })}
              allowCustomValue={false}
              size="xs"
              by="id"
              onOpenChange={(open) => {
                if (open === false) {
                  // Debounce setting the combobox open state to false to avoid transition flickering
                  setTimeout(() => {
                    setComboboxOpen(false);
                  }, 200);
                } else {
                  setComboboxOpen(true);
                }
              }}
            />
            <Tooltip
              content={showCopySuccess ? "Copied" : "Copy to clipboard"}
              delayDuration={150}
              keepOpenOnActivation={true}
            >
              <UnstyledButtonBase
                onClick={handleCopy}
                className="group/icon-button rounded-ms p-4 hover:bg-background-base-3"
                disabled={showCopySuccess}
              >
                {showCopySuccess ? (
                  <Check className="h-12 w-12 fill-icon-success-2" />
                ) : (
                  <Copy className="h-12 w-12 fill-icon-base-2 group-hover/icon-button:fill-icon-base-1" />
                )}
              </UnstyledButtonBase>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Negative margin to offset the sticky language select */}
      <div
        className={classNames(
          "pointer-events-auto min-w-0 whitespace-pre-wrap text-13-20",
          // ...and here (see negative margin above) to position the text part correctly.
          //  If the language select is inside, then we just move the text up to account for the sticky language select.
          languageSelectLocation.startsWith("top") ? "" : "-mt-24",
        )}
      >
        {language === "markdown" ? (
          <SafeMarkdownStyledWithCode
            key={`markdown-block`}
            className={classNames(containerClassName, codePreviewProps.className)}
          >
            {codeString}
          </SafeMarkdownStyledWithCode>
        ) : language === "plaintext" ? (
          <>{codeString}</>
        ) : language === "json-collapsible" ? (
          <JSONViewer value={value} defaultInspectDepth={3} editable={false} rootName={false} className="text-12-12" />
        ) : language === "yaml" ? (
          <CodePreview language={"yaml"} {...codePreviewProps}>
            {yaml.stringify(value, null, 2)}
          </CodePreview>
        ) : (
          <CodePreview language={language} {...codePreviewProps}>
            {codeString}
          </CodePreview>
        )}
      </div>
    </div>
  );
};
